import {
  Group, Mesh, SphereGeometry, MeshBasicMaterial, Vector3, Raycaster, Vector2, type Camera,
} from "three";
import { ParticlePool, type BosonKind } from "./particles";
import { detectFromBrowser, settingsForTier } from "../engine/tier";

interface VisRec { mesh: Mesh; pid: number; }

export class BosonEmitter {
  pool: ParticlePool;
  group = new Group();
  private vis: VisRec[] = [];
  private nextId = 1;
  private armed: BosonKind = "photon";
  private ray = new Raycaster();
  private ndc = new Vector2();

  constructor(private camera: Camera, private canvas: HTMLCanvasElement) {
    const cap = settingsForTier(detectFromBrowser()).sandboxMaxParticles;
    this.pool = new ParticlePool(cap);
  }

  arm(kind: BosonKind) { this.armed = kind; }

  attach(scene: { add(o: Group): void }) { scene.add(this.group); }

  bindClicks() {
    this.canvas.addEventListener("pointerdown", (e) => {
      const target = e.target as HTMLElement | null;
      if (target && target.closest("#hud")) return;
      this.ndc.x = (e.clientX / window.innerWidth) * 2 - 1;
      this.ndc.y = -(e.clientY / window.innerHeight) * 2 + 1;
      this.ray.setFromCamera(this.ndc, this.camera);
      const dir = this.ray.ray.direction.clone().normalize();
      const origin = this.camera.position.clone();
      this.fire(this.armed, origin, dir);
    });
  }

  fire(kind: BosonKind, origin: Vector3, dir: Vector3) {
    const id = this.nextId++;
    const ttl = kind === "w" ? 2 : kind === "higgs" ? 3 : Infinity;
    const speed = kind === "w" ? 5 : kind === "higgs" ? 0 : 8;
    this.pool.add({
      id, kind, born: 0, ttl, age: 0,
      pos: [origin.x, origin.y, origin.z],
      vel: [dir.x * speed, dir.y * speed, dir.z * speed],
    });
    const color = colorFor(kind);
    const mesh = new Mesh(
      new SphereGeometry(kind === "higgs" ? 0.4 : 0.08, 12, 12),
      new MeshBasicMaterial({ color, transparent: true }),
    );
    mesh.position.copy(origin);
    this.group.add(mesh);
    this.vis.push({ mesh, pid: id });
  }

  tick(dt: number) {
    this.pool.tick(dt);
    const live = new Set(this.pool.list().map((p) => p.id));
    for (const rec of [...this.vis]) {
      if (!live.has(rec.pid)) {
        this.group.remove(rec.mesh);
        rec.mesh.geometry.dispose();
        (rec.mesh.material as MeshBasicMaterial).dispose();
        this.vis.splice(this.vis.indexOf(rec), 1);
      }
    }
    for (const p of this.pool.list()) {
      p.pos[0] += p.vel[0] * dt;
      p.pos[1] += p.vel[1] * dt;
      p.pos[2] += p.vel[2] * dt;
      const rec = this.vis.find((v) => v.pid === p.id);
      if (rec) rec.mesh.position.set(p.pos[0], p.pos[1], p.pos[2]);
    }
  }

  reset() {
    for (const rec of this.vis) {
      this.group.remove(rec.mesh);
      rec.mesh.geometry.dispose();
      (rec.mesh.material as MeshBasicMaterial).dispose();
    }
    this.vis = [];
    this.pool.clear();
  }
}

function colorFor(k: BosonKind): number {
  switch (k) {
    case "photon": return 0xfff0aa;
    case "w": return 0xfff066;
    case "gluon": return 0xff66ff;
    case "higgs": return 0xff66aa;
    case "quark": return 0xff5566;
    case "electron": return 0x88aaff;
    case "neutrino": return 0xaaffaa;
  }
}
